import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { format, isToday, isPast, isFuture } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ContentCalendarEvent } from '../../services/contentManagement';

interface Props {
  events: ContentCalendarEvent[];
  onEdit: (event: ContentCalendarEvent) => void;
  onDelete: (id: string) => void;
  onApprove: (id: string) => void;
}

const ContentCalendar: React.FC<Props> = ({
  events,
  onEdit,
  onDelete,
  onApprove,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<ContentCalendarEvent | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon />;
      case 'facebook':
        return <FacebookIcon />;
      case 'youtube':
        return <YouTubeIcon />;
      default:
        return <EventIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'pending_approval':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const groupEventsByDate = () => {
    const grouped = events.reduce((acc, event) => {
      const date = format(new Date(event.date), 'yyyy-MM-dd');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(event);
      return acc;
    }, {} as { [key: string]: ContentCalendarEvent[] });

    return Object.entries(grouped).sort(([dateA], [dateB]) => 
      new Date(dateA).getTime() - new Date(dateB).getTime()
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        {groupEventsByDate().map(([date, dateEvents]) => (
          <Grid item xs={12} key={date}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {format(new Date(date), "EEEE, d 'de' MMMM", { locale: ptBR })}
                {isToday(new Date(date)) && (
                  <Chip 
                    label="Hoje" 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {dateEvents.map((event) => (
                <Grid item xs={12} md={6} lg={4} key={event.id}>
                  <Card 
                    sx={{ 
                      position: 'relative',
                      opacity: isPast(new Date(event.date)) ? 0.7 : 1
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getPlatformIcon(event.platform)}
                        <Typography variant="subtitle1" sx={{ ml: 1 }}>
                          {event.title}
                        </Typography>
                      </Box>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          size="small"
                          label={format(new Date(event.date), 'HH:mm')}
                          icon={<ScheduleIcon />}
                        />
                        <Chip
                          size="small"
                          label={event.status}
                          color={getStatusColor(event.status)}
                        />
                        <Chip
                          size="small"
                          label={event.type}
                        />
                      </Stack>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {event.content}
                      </Typography>

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        {event.status === 'pending_approval' && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => onApprove(event.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete(event.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent?.title}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <>
              <Typography variant="body1" paragraph>
                {selectedEvent.content}
              </Typography>
              {selectedEvent.media && selectedEvent.media.map((item) => (
                <Box key={item.id} sx={{ mt: 2 }}>
                  <img 
                    src={item.url} 
                    alt={item.title}
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Fechar
          </Button>
          {selectedEvent && (
            <Button
              color="primary"
              onClick={() => {
                onEdit(selectedEvent);
                setPreviewOpen(false);
              }}
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContentCalendar;
